import React, {
  HTMLAttributes,
  Ref,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useThemeCheck } from '../../utils';
import { Tooltip } from '../../Tooltip';

/**
 * Tooltip that is shown only when there is overflow.
 * @param children a function that accepts a ref and optional tooltip trigger props, and returns the tooltip trigger.
 * In order for tooltip to be shown when there is overflow, the ref must be set on the trigger (or
 * the part of the trigger that can have overflow). The tooltip trigger props should be spread onto
 * the actual DOM element that should trigger the tooltip (not wrapper components).
 * @param title tooltip content
 */

export interface TooltipIfNeededProps<R> {
  children: (
    ref: RefObject<R>,
    tooltipTriggerProps?: React.HTMLAttributes<HTMLElement> & {
      ref: React.Ref<R>;
    }
  ) => React.ReactElement;
  title?: React.ReactNode;
}

export const TooltipIfNeeded = <R extends HTMLElement>({
  children,
  title,
}: TooltipIfNeededProps<R>): React.ReactElement => {
  useThemeCheck();

  const ref = useRef<R>(null);
  const [needsTooltip, setNeedsTooltip] = useState(false);

  useEffect(() => {
    if (
      typeof process !== 'undefined' &&
      process.env.NODE_ENV !== 'production' &&
      !ref.current
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        "TooltipIfNeeded won't work as expected since the ref is not properly passed to the tooltip trigger."
      );
    }
  }, []);

  useEffect(() => {
    const elem = ref.current;
    if (!elem) {
      return;
    }
    const needsTooltip =
      elem.scrollWidth > elem.clientWidth ||
      elem.scrollHeight > elem.clientHeight;
    setNeedsTooltip(needsTooltip);
  });

  return needsTooltip && title ? (
    <Tooltip title={title}>
      {(tooltipTriggerProps) =>
        children(
          ref,
          // There props need to match the ones in `Tooltip` cloneElement call
          tooltipTriggerProps as HTMLAttributes<R> & { ref: Ref<R> }
        )
      }
    </Tooltip>
  ) : (
    <>{children(ref)}</>
  );
};
